/**
 * The standard zuvo event taxonomy (Zuvo Standard, section 8).
 *
 * Every product emits these events server-side into its `events` outbox table. The worker forwards
 * them to the Zuvo Hub as `ZuvoEvent` payloads once `ZUVO_HUB_URL` is set. The browser mirrors the
 * conversion events (those with a GA4 name) to Google Tag Manager via `pushDataLayer`.
 *
 * Changing this list is a breaking change for the Hub: bump `HUB_EVENT_SCHEMA_VERSION`.
 */

import type { DeploymentEnv } from "./env";

/** Version of the `ZuvoEvent` payload contract with the Zuvo Hub. */
export const HUB_EVENT_SCHEMA_VERSION = 1;

export const ZUVO_EVENTS = [
  "user.signed_up",
  "user.email_verified",
  "user.logged_in",
  "user.mfa_enabled",
  "user.mfa_disabled",
  "user.deleted",
  "workspace.created",
  "workspace.member_invited",
  "workspace.member_joined",
  "workspace.member_removed",
  "billing.checkout_started",
  "billing.subscription_started",
  "billing.subscription_changed",
  "billing.subscription_canceled",
  "billing.payment_failed",
  "product.activated",
  "product.feature_used",
  "lead.waitlist_joined",
  "lead.contact_submitted",
] as const;

export type ZuvoEventName = (typeof ZUVO_EVENTS)[number];

export type BillingInterval = "monthly" | "yearly";
export type WorkspaceRole = "owner" | "admin" | "member";
export type MfaMethod = "totp" | "email";

/** Monetary fields use major currency units (CHF 29.00 is `29`), like GA4. Stripe amounts are in cents. */
export interface BillingAmount {
  /** Plan key from zuvo.yaml, e.g. "pro". */
  plan: string;
  interval: BillingInterval;
  /** Amount in major units, e.g. 29 for CHF 29.00. */
  amount: number;
  /** ISO 4217, uppercase, e.g. "CHF". */
  currency: string;
}

/** Typed properties per event. Extra keys are allowed by `ZuvoEvent.properties` but not required. */
export interface ZuvoEventProperties {
  "user.signed_up": { method?: string };
  "user.email_verified": Record<string, never>;
  "user.logged_in": { method?: string; mfa?: boolean };
  "user.mfa_enabled": { method: MfaMethod };
  "user.mfa_disabled": { method?: MfaMethod };
  "user.deleted": { reason?: string };
  "workspace.created": Record<string, never>;
  "workspace.member_invited": { role: WorkspaceRole };
  "workspace.member_joined": { role: WorkspaceRole };
  "workspace.member_removed": { role?: WorkspaceRole };
  "billing.checkout_started": BillingAmount;
  "billing.subscription_started": BillingAmount & { subscriptionId?: string };
  "billing.subscription_changed": {
    fromPlan?: string;
    plan: string;
    interval?: BillingInterval;
    amount?: number;
    currency?: string;
  };
  "billing.subscription_canceled": { plan: string; reason?: string; atPeriodEnd?: boolean };
  "billing.payment_failed": { plan?: string; amount?: number; currency?: string; attempt?: number };
  "product.activated": { milestone?: string };
  "product.feature_used": { feature: string };
  "lead.waitlist_joined": { source?: string; locale?: string };
  "lead.contact_submitted": { source?: string; topic?: string };
}

/** The payload the Zuvo Hub receives. */
export interface ZuvoEvent {
  /** UUID, used by the Hub to deduplicate retries. */
  id: string;
  name: ZuvoEventName;
  /** ISO 8601 timestamp. */
  occurredAt: string;
  /** Product slug from zuvo.yaml. */
  product: string;
  environment: DeploymentEnv;
  userId?: string | null;
  workspaceId?: string | null;
  properties: Record<string, unknown>;
}

export interface CreateZuvoEventInput<N extends ZuvoEventName> {
  name: N;
  product: string;
  environment: DeploymentEnv;
  properties: ZuvoEventProperties[N];
  userId?: string | null;
  workspaceId?: string | null;
  id?: string;
  occurredAt?: Date | string;
}

export function isZuvoEventName(value: string): value is ZuvoEventName {
  return (ZUVO_EVENTS as readonly string[]).includes(value);
}

/** Builds a `ZuvoEvent` with a fresh id and timestamp. */
export function createZuvoEvent<N extends ZuvoEventName>(input: CreateZuvoEventInput<N>): ZuvoEvent {
  const occurredAt =
    input.occurredAt instanceof Date ? input.occurredAt.toISOString() : (input.occurredAt ?? nowIso());
  return {
    id: input.id ?? randomId(),
    name: input.name,
    occurredAt,
    product: input.product,
    environment: input.environment,
    userId: input.userId ?? null,
    workspaceId: input.workspaceId ?? null,
    properties: { ...input.properties },
  };
}

const GA4_EVENTS: Partial<Record<ZuvoEventName, string>> = {
  "user.signed_up": "sign_up",
  "user.logged_in": "login",
  "billing.checkout_started": "begin_checkout",
  "billing.subscription_started": "purchase",
  "lead.waitlist_joined": "generate_lead",
  "lead.contact_submitted": "generate_lead",
};

/** GA4 recommended event name for a zuvo event, or null when the event is not mirrored to GA4. */
export function ga4EventFor(name: ZuvoEventName): string | null {
  return GA4_EVENTS[name] ?? null;
}

const ECOMMERCE_EVENTS = new Set<string>(["begin_checkout", "purchase"]);

/**
 * Maps a zuvo event to GA4 event parameters. Returns an empty object for events without a GA4
 * equivalent. Ecommerce events get `value`, `currency` and `items` as GA4 expects.
 */
export function toGa4Params(event: Pick<ZuvoEvent, "name" | "properties">): Record<string, unknown> {
  const ga4 = ga4EventFor(event.name);
  const p = event.properties;
  if (ga4 === null) return {};

  if (ECOMMERCE_EVENTS.has(ga4)) {
    const plan = str(p.plan);
    const interval = str(p.interval);
    const amount = num(p.amount);
    const currency = str(p.currency)?.toUpperCase();
    const params: Record<string, unknown> = {};
    if (amount !== undefined) params.value = amount;
    if (currency !== undefined) params.currency = currency;
    const transactionId = str(p.subscriptionId);
    if (ga4 === "purchase" && transactionId !== undefined) params.transaction_id = transactionId;
    if (plan !== undefined) {
      const item: Record<string, unknown> = { item_id: plan, item_name: plan, quantity: 1 };
      if (interval !== undefined) item.item_variant = interval;
      if (amount !== undefined) item.price = amount;
      params.items = [item];
    }
    return params;
  }

  if (ga4 === "sign_up" || ga4 === "login") {
    return { method: str(p.method) ?? "email" };
  }

  if (ga4 === "generate_lead") {
    const params: Record<string, unknown> = {
      lead_type: event.name === "lead.waitlist_joined" ? "waitlist" : "contact",
    };
    const source = str(p.source);
    if (source !== undefined) params.lead_source = source;
    return params;
  }

  return {};
}

interface DataLayerHost {
  window?: unknown;
  dataLayer?: unknown;
}

/**
 * Mirrors a zuvo event to the GTM `dataLayer` in the browser. Pushes `{ event, zuvo_event, ...params }`
 * where `event` is the GA4 name when one exists, otherwise the zuvo name. Ecommerce events are pushed
 * in GA4 ecommerce shape (`ecommerce: { value, currency, items }`, preceded by `{ ecommerce: null }`).
 *
 * Safe no-op on the server and when GTM is not loaded (`window.dataLayer` missing). Returns whether
 * anything was pushed.
 */
export function pushDataLayer<N extends ZuvoEventName>(name: N, properties: ZuvoEventProperties[N]): boolean {
  const host = globalThis as DataLayerHost;
  if (typeof host.window === "undefined") return false;
  const dataLayer = host.dataLayer;
  if (!Array.isArray(dataLayer)) return false;

  const ga4 = ga4EventFor(name);
  const params = toGa4Params({ name, properties: { ...properties } });
  if (ga4 !== null && ECOMMERCE_EVENTS.has(ga4)) {
    dataLayer.push({ ecommerce: null });
    dataLayer.push({ event: ga4, zuvo_event: name, ecommerce: params });
  } else {
    dataLayer.push({ event: ga4 ?? name, zuvo_event: name, ...properties, ...params });
  }
  return true;
}

function str(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

function num(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function nowIso(): string {
  return new Date().toISOString();
}

function randomId(): string {
  const cryptoLike = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (cryptoLike?.randomUUID) return cryptoLike.randomUUID();
  // Fallback for very old runtimes: RFC 4122 v4 shape from Math.random (not for security use).
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}
